<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="flatfinder-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #center>
          <transition name="fade">
            <DraftAlert v-if="hasActiveDraft" />
          </transition>
        </template>
        <template #right>
          <div class="row wrap g-1">
            <Actions
              placement="flatfinder"
              :query="{ flatfinder: id, 'kvass-draft': draft || showDraft }"
              @action:refetch="onActionRefetch"
            />

            <ButtonComponent
              v-if="!showDraft || isNew"
              :key="isNew ? 'create' : 'save'"
              :label="saveState.save.label"
              :success-label="$t('saved')"
              :loading-label="$t('saving')"
              :icon="['fal', saveState.save.icon]"
              :theme="saveState.save.theme"
              :promise="promises.mutate"
              :class="{
                'elder-button--disabled': saveState.save.disabled,
                'elder-button--invalid': saveState.save.invalid,
              }"
              v-tooltip="{ content: saveState.save.tooltip }"
              @click="submit"
            >
            </ButtonComponent>

            <Draft
              v-else
              v-model="draft"
              :saveState="saveState"
              :reference="{ ref: project, onModel: 'Project' }"
              :submit="submit"
              @reset="() => fetch()"
            />
          </div>
        </template>
      </HeaderComponent>

      <StepperComponent :value="steps" ref="stepper" state-handler="route" @submit="submit">
        <template #after="{ meta, next, prev }">
          <div class="row g-1 center">
            <ButtonComponent
              v-if="meta.prev"
              :label="$t('prev')"
              type="submit"
              :icon="['fal', 'angle-left']"
              icon-placement="left"
              @click="onNavigation(prev)"
            />
            <ButtonComponent
              v-if="meta.next"
              :label="$t('next')"
              type="submit"
              :icon="['fal', 'angle-right']"
              @click="onNavigation(next)"
            />
            <ButtonComponent
              v-if="!meta.next"
              :label="$t('complete')"
              :success-label="$t('completed')"
              :loading-label="$t('saving')"
              :icon="['fal', 'check']"
              theme="primary"
              type="button"
              :disabled="!validator.passes"
              :promise="promises.mutate"
              @click="complete"
            />
          </div>
        </template>
      </StepperComponent>
      <ModalComponent :show="showResidentialWarning" class="flatfinder-mutate__residential-warning">
        <Card theme="flat">
          <template #header>
            <FontAwesomeIcon :icon="['fal', 'exclamation-triangle']" size="3x" />
            <strong class="mt-1">{{ $t('flatfinderCreateResidentialWarningTitle') }}</strong>
          </template>
          <template #default>
            <p>{{ $t('flatfinderCreateResidentialWarning') }}</p>
            <p class="fade text--small">({{ $t('flatfinderCreateResidentialWarningException') }})</p>
          </template>
          <template #footer>
            <ButtonComponent
              @click="
                $router.push({
                  name: 'project-edit',
                  params: { id: $path('project.id', item), step: 'flatfinders' },
                })
              "
              :label="$t('cancel')"
              :icon="['fal', 'times']"
            />
            <ButtonComponent
              theme="primary"
              @click="ignoreResidentialWarning = true"
              :label="$t('continue')"
              :icon="['fal', 'angle-right']"
            />
          </template>
        </Card>
      </ModalComponent>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'
import ModelMixin from '@/mixins/Model'
import Model from '@/models/Flatfinder'
import moment from 'moment'

import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import { StepperComponent } from 'vue-elder-stepper'
import { ModalComponent } from 'vue-elder-modal'
import Steps from './Steps'
import Actions from '@/components/Actions'
import DraftAlert from '@/components/Draft/Alert'
import Draft from '@/components/Draft/Component'

import defaultFields from '@/components/Flatfinder/StepsFields/defaults.js'
import fields from '@/components/Flatfinder/StepsFields/fields.js'

import { buildStepsFields } from '@/utils'
import { mergeDeepRight } from 'ramda'
import { getValidatorConfig } from '@kvass/custom-fields'

const normalizeDate = value => {
  if (!value) return null
  return moment.utc(value).format('YYYY-MM-DD')
}

const normalizeFrames = frames =>
  (frames || []).map(frame => ({
    ...frame,
    items: (frame.items || []).map(item => {
      const { reference, data } = item
      const { onModel, ...ref } = reference || {}
      return { data, reference: { onModel, ref } }
    }),
    sunlightConditions: (frame.sunlightConditions || []).map(condition => ({
      ...condition,
      date: normalizeDate(condition.date),
    })),
  }))

const normalizeFlatfinder = flatfinder => {
  if (!flatfinder) return flatfinder
  return {
    ...flatfinder,
    frames: normalizeFrames(flatfinder.frames),
  }
}

export default {
  mixins: [
    Validator({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      source: 'item',
      model: Model,
      guard: true,
      preTransform: {
        frames: v => (v && v.length ? v : [{}]),
      },
      transform: {
        'customFieldsConfig.*.schema': undefined,
      },
    }),
  ],
  props: {
    id: String,
    step: String,
    project: String,
  },
  data() {
    return {
      ignoreResidentialWarning: false,
      draft: true,
      initialLoad: false,
    }
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  computed: {
    ...mapState('Flatfinder', ['item', 'promises', 'states']),

    labels() {
      return {
        ...mapi18n({
          name: 'name',
          'frames.0.image': ['image', 1],
        }),
        ...this.customFieldValidatorConfig.labels,
      }
    },

    rules() {
      let rules = {
        name: 'required',
        'frames.*.image': 'required',
        ...this.customFieldValidatorConfig.rules,
      }

      return rules
    },

    showResidentialWarning() {
      if (this.ignoreResidentialWarning) return
      return !this.states.fetchOne && !this.$path('item.project.stats.count')
    },

    hasActiveDraft() {
      return typeof this.draft === 'string'
    },

    showDraft() {
      return this.$path('item.project.metadata.draftAvailable') || this.hasActiveDraft
    },
    isNew() {
      return !this.id
    },
    title() {
      return this.item.name || this.$t('loading', { resource: this.$tc('unitSelector').toLowerCase() }) + '...'
    },
    breadcrumbs() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.project.name'),
          action: {
            name: 'project-dashboard',
            params: { id: this.$path('item.project.id') },
          },
        },
        {
          title: this.$tc('unitSelector', 2),
          action: {
            name: 'project-edit',
            params: {
              id: this.$path('item.project.id'),
              step: 'flatfinders',
            },
          },
        },
        { title: this.$metaInfo.title },
      ]
    },

    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}
      return getValidatorConfig(this.customFieldSchemas, this.data, { prefix: 'customFields' })
    },

    customFieldSchemas() {
      // delay load of customFieldsSchemas, so schemas populate with data correctly.
      if (!this.initialLoad) return []

      return [
        ...Array.from(defaultFields.values() || []).map(i => i(this)),
        ...(this.$path('item.customFieldsConfig') || [])
          .map(c => c.schema.map(e => ({ ...e, placement: c.placement || e.placement })))
          .flat(),
      ]
    },

    saveState() {
      let base = {
        save: {
          label: this.$t(
            this.isNew ? (this.showDraft ? 'createDraft' : 'create') : this.showDraft ? 'saveDraft' : 'save',
          ),

          icon: 'arrow-to-bottom',
          disabled: !this.validator.passes || !this.hasChanges,
          theme: this.showDraft ? 'secondary' : 'primary',
        },
        publish: {
          label: this.$t('publish'),
          icon: 'chevron-down',
          disabled: !this.validator.passes || (!this.hasChanges && !this.draft),
          theme: 'secondary',
        },
      }

      if (!this.hasChanges) {
        base.save.tooltip = this.$t('noUnsavedChanges')

        if (!this.draft) {
          base.publish.tooltip = this.$t('noUnsavedChanges')
        }
      }

      if (!this.states.fetchOne && !this.validator.passes) {
        const invalid = {
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('<br>'),
        }

        Object.keys(base).forEach(key => {
          base[key] = { ...base[key], ...invalid }
        })
      }

      return base
    },

    steps() {
      const data = mergeDeepRight(this.item, { ...this.data })

      let props = {
        value: this.data,
        data,
        labels: this.labels,
        validator: this.validator,
        promises: this.promises,
        project: this.project,
        customFieldSchemas: this.customFieldSchemas,
        fields: buildStepsFields(Array.from(fields.values()), this.customFieldSchemas, this),
      }

      let listeners = {
        submit: () => this.submit(),
      }

      return Steps().map(raw => {
        const { icon, ...s } = raw
        s.props = {
          ...(s.props || {}),
          ...props,
          fields: {
            ...props.fields,
            default: props.fields.default.filter(i => i.defaultPlacement === raw.id),
          },
        }
        s.listeners = {
          ...(s.listeners || {}),
          ...listeners,
        }
        s.disabled = s.id == 'builder' && this.isNew
        return s
      })
    },
    preventSubmit() {
      return !this.validator.passes || !this.hasChanges
    },
  },

  methods: {
    ...mapActions('Flatfinder', {
      fetch(dispatch) {
        return dispatch('fetch', [
          { id: this.id || '', project: this.project, isNew: this.isNew },
          {
            query: 'ProjectFlatfinder',
            transform: v => {
              let base = { project: v.Reference }
              if (!v.Flatfinder) return base
              return {
                ...base,
                ...normalizeFlatfinder(v.Flatfinder),
              }
            },
            draft: this.draft,
          },
        ]).then(data => {
          const setDraft = () => {
            if (data.project.draftId) return data.project.draftId
            return this.isNew ? this.$path('item.project.metadata.draftAvailable') : false
          }

          this.draft = setDraft()

          this.initialLoad = true
        })
      },
      mutate: 'mutate',
      remove: 'remove',
    }),
    onActionRefetch(payload) {
      if (payload && payload.draftId) this.draft = payload.draftId
      return this.fetch()
    },
    submit(options = {}) {
      const { force = false } = options
      if (!force && this.preventSubmit) return Promise.resolve()

      let data = this.getData()
      if (this.isNew) data.project = this.project
      if (data.parent) data.parent = data.parent.id

      data.frames = (data.frames || []).map(frame => ({
        image: frame.image,
        cardinalDirection: frame.cardinalDirection,
        items: (frame.items || [])
          .map(item => {
            const ref = this.$path('reference.ref.id', item)
            if (!ref) return null

            return {
              data: item.data,
              reference: { onModel: item.reference.onModel, ref },
            }
          })
          .filter(Boolean),
        sunlightConditions: frame.sunlightConditions || [],
      }))

      return this.mutate([
        data,
        {
          transformPayload: true,
          arrayStrategy: 'default',
          transform: v => normalizeFlatfinder(v[`Flatfinder${this.isNew ? 'Create' : 'Update'}`]),
          draft: this.draft,
        },
      ]).then(res => {
        // set draft id after autosave
        if (this.draft === true && this.$path('project.draftId', res)) this.draft = this.$path('project.draftId', res)

        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)

        this.$router.replace({
          name: 'project-flatfinder-edit',
          params: { id: res.id, project: this.project, step: 'builder' },
        })
      })
    },
    complete(options) {
      if (!this.draft) this.draft = this.showDraft

      return this.submit(options).then(() =>
        this.$router.push({ name: 'project-edit', params: { id: this.project, step: 'flatfinders' } }),
      )
    },
    onNavigation(callback) {
      callback()
      if (this.preventSubmit) return
      // set draft so autosaves to draft
      if (!this.draft) this.draft = this.showDraft
      this.submit()
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createUnitSelector') : this.title,
    }
  },
  components: {
    HeaderComponent,
    ModalComponent,
    StepperComponent,
    Card,
    Actions,
    DraftAlert,
    Draft,
  },
}
</script>

<style lang="scss">
.flatfinder-mutate {
  background-color: $background-grey;

  .elder-stepper__component > * {
    margin: 2rem auto;
  }

  .elder-stepper[data-current-step='basis'] .project-flatfinder-mutate-basis,
  .elder-stepper[data-current-step='builder'] .project-flatfinder-mutate-builder {
    animation: fadeInFromRight 300ms ease;
  }

  .elder-modal {
    z-index: get-layer('modal');
  }

  &__residential-warning {
    .card__header {
      background-color: css-alpha('warning', 0.2);
      color: var(--warning);
      min-height: 170px;
    }

    .card__content {
      text-align: center;
      max-width: 400px;
    }

    .card__footer-content {
      justify-content: center;
    }
  }
}
</style>
